import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLucky6Access } from 'hooks/useLucky6Access';
import { useKenoAccess } from 'hooks/useKenoAccess';
import classNames from 'classnames';
import useAsyncEffect from 'use-async-effect';
import { UserContext } from 'context/userContext';
import { SlipContext } from 'context/slipContext';
import { BrandLogo } from 'components/BrandLogo/BrandLogo';
import { Footer } from 'components/Footer/Footer';
import { ThemeMenuIcon } from 'components/ThemeMenuIcon/ThemeMenuIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { SportsBettingRightSide } from 'components/SportsBettingRightSide/SportsBettingRightSide';
import { ModernDrawer } from 'components/mobile/ModernChrome/ModernChrome';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { usePlayerLayout } from 'lib/playerLayout';
import { formatBalanceDisplay } from 'helpers/balanceFormat';
import { sportTranslationKey } from 'helpers/sportI18n';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { getSports } from 'services/api/sportBetsService';
import type { MenuIconKey } from 'lib/menuIcons';
import type { Sport } from 'types/Sport';
import styles from './ModernDesktopShell.module.css';

const CURRENCY = process.env.REACT_APP_CURRENCY ?? '';

/** True on a wide screen with the modern layout: the shell owns the sidebar
 *  and the slip column, so pages drop their own. */
export function useModernDesktop() {
  const { isMobile } = useBreakpoint();
  return usePlayerLayout() === 'modern' && !isMobile;
}

type SidebarSlot = {
  slot: HTMLElement | null;
  claim: (on: boolean) => void;
};

const SidebarContext = createContext<SidebarSlot | null>(null);

/** Renders `children` in the shell's sport sidebar (instead of the default
 *  sport list), inside the page's own contexts. Nothing outside the shell. */
export function ModernSidebarPortal({ children }: { children: ReactNode }) {
  const ctx = useContext(SidebarContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.claim(true);
    return () => ctx.claim(false);
  }, [ctx]);
  return ctx?.slot ? createPortal(children, ctx.slot) : null;
}

const NAV: { icon: MenuIconKey; label: string; to: string }[] = [
  { icon: 'home', label: 'home_label', to: '/' },
  { icon: 'live-betting', label: 'live_betting_label', to: '/live-betting' },
  { icon: 'casino-games', label: 'casino_games_label', to: '/casino-games' },
  { icon: 'live-casino', label: 'live_casino_label', to: '/live-casino' },
  { icon: 'casino-games', label: 'lucky_six_label', to: '/lucky-six' },
  { icon: 'casino-games', label: 'keno_label', to: '/keno' },
  { icon: 'results', label: 'results_label', to: '/results' },
];

function DefaultSports() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sports, setSports] = useState<Sport[]>([]);
  useAsyncEffect(async () => {
    const s = await getSports(undefined).catch(() => []);
    setSports(Array.isArray(s) ? s : []);
  }, []);
  return (
    <>
      {sports.map((s) => (
        <button
          type="button"
          key={s.id}
          className={styles.sportRow}
          onClick={() => navigate('/sports-betting')}
        >
          <SportIcon
            sportId={s.id}
            className={styles.sportIcon}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getDefaultSportIconSrc();
            }}
          />
          <span>{t(sportTranslationKey(s.id), { defaultValue: s.name })}</span>
          {!!s.count && <em>{s.count}</em>}
        </button>
      ))}
    </>
  );
}

/** Desktop frame of the modern layout: sidebar with navigation and sports,
 *  a top bar with the logo and the balance pill (account menu), the page,
 *  and the slip docked at the bottom. */
export function ModernDesktopShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const url = useLocation().pathname;
  const { loggedUser } = useContext(UserContext);
  const { entries, maxCoefficient, removeAllEntries, getTotalBets } =
    useContext(SlipContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const lucky6 = useLucky6Access();
  const keno = useKenoAccess();
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [claimed, setClaimed] = useState(0);
  const claim = useCallback(
    (on: boolean) => setClaimed((n) => Math.max(0, n + (on ? 1 : -1))),
    []
  );
  const sidebar = useMemo(() => ({ slot, claim }), [slot, claim]);

  useEffect(() => setMenuOpen(false), [url]);

  // The slip is a bar docked at the bottom of the page, opening upwards
  // (as on the big bookmakers' sites): a column beside the offer took a
  // fifth of the screen even while empty. It shows once there is a pick.
  const picks = entries?.length ?? 0;
  const [slipOpen, setSlipOpen] = useState(false);
  const [payout, setPayout] = useState(0);
  useEffect(() => {
    if (picks === 0) setSlipOpen(false);
  }, [picks]);
  const slipDock = picks > 0 && (
    <div className={classNames(styles.slipDock, slipOpen && styles.slipOpen)}>
      <div className={styles.slipBar}>
        <button
          type="button"
          className={styles.slipToggle}
          onClick={() => setSlipOpen((o) => !o)}
          aria-expanded={slipOpen}
        >
          <span className={styles.slipCount}>{getTotalBets()}</span>
          <span className={styles.slipTitle}>
            <strong>{t('bet_slip_label')}</strong>
            {!slipOpen && maxCoefficient > 0 && (
              <small>
                {t('quota_label')} {Number(maxCoefficient).toFixed(2)}
                {payout > 0 && (
                  <>
                    {' › '}
                    {payout.toFixed(2)} {CURRENCY}
                  </>
                )}
              </small>
            )}
          </span>
        </button>
        <button
          type="button"
          className={styles.slipClear}
          onClick={() => removeAllEntries()}
        >
          {t('remove_all_bets_label')}
        </button>
        <button
          type="button"
          className={styles.slipChevron}
          onClick={() => setSlipOpen((o) => !o)}
          aria-label={t('bet_slip_label')}
        >
          {slipOpen ? '▾' : '▴'}
        </button>
      </div>
      {/* Kept mounted while closed: the slip holds the stake typed in. */}
      <div className={styles.slipBody} hidden={!slipOpen}>
        <SportsBettingRightSide
          slipEntries={entries}
          inShell
          onPayout={setPayout}
        />
      </div>
    </div>
  );

  const active = (to: string) =>
    to === '/' ? url === '/' : url === to || url.startsWith(to + '/');

  return (
    <SidebarContext.Provider value={sidebar}>
      <div className={classNames(styles.shell, picks > 0 && styles.withDock)}>
        <aside className={styles.sidebar}>
          <nav className={styles.nav}>
            {NAV.filter(
              (n) =>
                (n.to !== '/lucky-six' || lucky6) && (n.to !== '/keno' || keno)
            ).map((n) => (
              <button
                type="button"
                key={n.to}
                className={classNames(
                  styles.navItem,
                  active(n.to) && styles.navActive
                )}
                onClick={() => navigate(n.to)}
              >
                <ThemeMenuIcon icon={n.icon} alt="" variant="footer" />
                <span>{t(n.label)}</span>
              </button>
            ))}
          </nav>
          <p className={styles.sectionTitle}>{t('modern_nav_sports')}</p>
          <div ref={setSlot} className={styles.sportSlot} />
          {claimed === 0 && <DefaultSports />}
        </aside>

        <div className={styles.main}>
          <header className={styles.topbar}>
            <BrandLogo className={styles.logo} onClick={() => navigate('/')} />
            <div className={styles.account}>
              {loggedUser ? (
                <button
                  type="button"
                  className={styles.pill}
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-expanded={menuOpen}
                >
                  <span className={styles.avatar}>
                    <ThemeMenuIcon icon="person" alt="" variant="footer" />
                  </span>
                  <span className={styles.pillText}>
                    <small>{loggedUser.username}</small>
                    <strong>
                      {formatBalanceDisplay(loggedUser.balance)}{' '}
                      <em>{CURRENCY}</em>
                    </strong>
                  </span>
                  <span className={styles.caret}>{menuOpen ? '▴' : '▾'}</span>
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.login}
                  onClick={() => navigate('/login')}
                >
                  {t('login_label')}
                </button>
              )}
              {menuOpen && (
                <ModernDrawer dropdown close={() => setMenuOpen(false)} />
              )}
            </div>
          </header>
          <div className={styles.content}>{children}</div>
          <Footer />
        </div>

        {slipDock}
      </div>
    </SidebarContext.Provider>
  );
}
